import React, { useState } from 'react';
import { FaCalculator, FaRobot, FaCheckCircle, FaArrowRight, FaArrowLeft, FaDownload, FaWhatsapp, FaStar } from 'react-icons/fa';
import recommendationEngine from '../services/recommendationEngine';
import aiService from '../services/aiService';
import WhatsAppHelper from '../utils/whatsappHelper';
import PDFGenerator from '../utils/pdfGenerator';
import './SystemPlanner.css';

const SystemPlanner = () => {
  const [mode, setMode] = useState(null); // 'form' or 'chatbot'
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  
  const [formData, setFormData] = useState({
    venueType: '',
    length: '',
    width: '',
    height: '',
    capacity: '',
    usage: [],
    budget: 'mid-range'
  });

  const [chatHistory, setChatHistory] = useState([
    { role: 'assistant', content: 'Hi! I\'m here to help design your perfect PA system. What type of venue do you have?' }
  ]);
  const [chatInput, setChatInput] = useState('');

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUsageChange = (usage) => {
    setFormData(prev => ({
      ...prev,
      usage: prev.usage.includes(usage)
        ? prev.usage.filter(u => u !== usage)
        : [...prev.usage, usage]
    }));
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        return formData.venueType !== '';
      case 2:
        return formData.length && formData.width && formData.height && formData.capacity;
      case 3:
        return formData.usage.length > 0;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      // Get recommendations from engine
      const recs = recommendationEngine.getRecommendations({
        ...formData,
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        capacity: parseInt(formData.capacity)
      });

      // Enhance with AI explanations (if configured)
      if (aiService.isConfigured()) {
        for (let rec of recs) {
          const aiExplanation = await aiService.generateExplanation(rec, formData);
          rec.aiExplanation = aiExplanation;
        }
      }

      setRecommendations(recs);
      setStep(4);
    } catch (error) {
      console.error('Recommendation error:', error);
      alert('Error generating recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Chatbot handlers
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    
    // Add user message to history
    const newHistory = [...chatHistory, { role: 'user', content: userMessage }];
    setChatHistory(newHistory);

    // Get AI response
    setLoading(true);
    try {
      const response = await aiService.chatbotResponse(chatHistory, userMessage);
      setChatHistory([...newHistory, { role: 'assistant', content: response }]);

      // Check if we have enough info
      const extractedData = await aiService.extractVenueData([...newHistory, { role: 'assistant', content: response }]);
      if (extractedData && extractedData.venueType && extractedData.length && extractedData.capacity) {
        // Enough data collected, offer to show recommendations
        setTimeout(() => {
          setChatHistory(prev => [...prev, {
            role: 'assistant',
            content: 'Perfect! I have all the information I need. Would you like me to show you recommended systems? (Type "yes" or click the button below)'
          }]);
        }, 1000);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I\'m having trouble. Please try the form instead.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = (recommendation) => {
    PDFGenerator.generateQuotation(recommendation, formData);
  };

  const handleBookWhatsApp = (recommendation) => {
    WhatsAppHelper.bookPackage(recommendation, formData);
  };

  // Render functions
  const renderModeSelection = () => (
    <div className="mode-selection">
      <div className="planner-hero">
        <h1>🎯 AI-Powered System Planner</h1>
        <p className="hero-subtitle">Get professional PA system recommendations in 2 minutes</p>
      </div>

      <div className="mode-options">
        <div className="mode-card" onClick={() => setMode('form')}>
          <FaCalculator className="mode-icon" />
          <h3>Quick Form</h3>
          <p>Answer a few questions and get instant recommendations</p>
          <button className="mode-btn">Start Form <FaArrowRight /></button>
        </div>

        <div className="mode-card" onClick={() => setMode('chatbot')}>
          <FaRobot className="mode-icon" />
          <h3>AI Assistant</h3>
          <p>Chat with our AI to design your perfect system</p>
          <button className="mode-btn">Start Chat <FaArrowRight /></button>
        </div>
      </div>

      <div className="trust-indicators">
        <div className="trust-item">
          <FaCheckCircle /> 20+ Years Experience
        </div>
        <div className="trust-item">
          <FaCheckCircle /> 500+ Installations
        </div>
        <div className="trust-item">
          <FaCheckCircle /> Free Consultation
        </div>
      </div>
    </div>
  );

  const renderFormStep1 = () => (
    <div className="form-step">
      <button className="back-to-mode" onClick={() => setMode(null)}>
        <FaArrowLeft /> Back to Options
      </button>
      <h2>Step 1: Venue Type</h2>
      <p className="step-description">What type of space are you setting up?</p>

      <div className="venue-options">
        {[
          { value: 'church', label: '⛪ Church / Place of Worship', desc: 'Sermons, choir, worship music' },
          { value: 'auditorium', label: '🎭 Auditorium / Theatre', desc: 'Presentations, performances, events' },
          { value: 'function_hall', label: '🏛️ Function Hall / Banquet', desc: 'Weddings, parties, celebrations' },
          { value: 'corporate', label: '🏢 Corporate / Meeting Room', desc: 'Conferences, training, meetings' },
          { value: 'open_ground', label: '🌳 Open Ground / Outdoor', desc: 'Cultural events, festivals, concerts' }
        ].map(venue => (
          <div
            key={venue.value}
            className={`venue-option ${formData.venueType === venue.value ? 'selected' : ''}`}
            onClick={() => setFormData({ ...formData, venueType: venue.value })}
          >
            <h4>{venue.label}</h4>
            <p>{venue.desc}</p>
          </div>
        ))}
      </div>

      <div className="form-navigation">
        <button onClick={handleNext} disabled={!formData.venueType} className="btn-primary">
          Next <FaArrowRight />
        </button>
      </div>
    </div>
  );

  const renderFormStep2 = () => (
    <div className="form-step">
      <button className="back-to-step" onClick={handleBack}>
        <FaArrowLeft /> Back
      </button>
      <h2>Step 2: Venue Dimensions</h2>
      <p className="step-description">Tell us about your space</p>

      <div className="input-grid">
        <div className="input-group">
          <label>Length (feet) *</label>
          <input
            type="number"
            name="length"
            value={formData.length}
            onChange={handleInputChange}
            placeholder="e.g., 60"
            min="10"
            required
          />
        </div>

        <div className="input-group">
          <label>Width (feet) *</label>
          <input
            type="number"
            name="width"
            value={formData.width}
            onChange={handleInputChange}
            placeholder="e.g., 40"
            min="10"
            required
          />
        </div>

        <div className="input-group">
          <label>Height (feet) *</label>
          <input
            type="number"
            name="height"
            value={formData.height}
            onChange={handleInputChange}
            placeholder="e.g., 18"
            min="8"
            required
          />
        </div>

        <div className="input-group">
          <label>Seating Capacity *</label>
          <input
            type="number"
            name="capacity"
            value={formData.capacity}
            onChange={handleInputChange}
            placeholder="e.g., 300"
            min="10"
            required
          />
        </div>
      </div>

      {formData.length && formData.width && (
        <div className="calculated-area">
          <FaCalculator /> Calculated Area: <strong>{formData.length * formData.width} sq.ft</strong>
        </div>
      )}

      <div className="form-navigation">
        <button onClick={handleBack} className="btn-secondary">Back</button>
        <button onClick={handleNext} disabled={!validateStep(2)} className="btn-primary">
          Next <FaArrowRight />
        </button>
      </div>
    </div>
  );

  const renderFormStep3 = () => (
    <div className="form-step">
      <button className="back-to-step" onClick={handleBack}>
        <FaArrowLeft /> Back
      </button>
      <h2>Step 3: Usage & Budget</h2>
      <p className="step-description">How will you use this system?</p>

      <div className="usage-section">
        <h4>Primary Usage (Select all that apply)</h4>
        <div className="usage-options">
          {[
            { value: 'speech', label: '🎤 Speech / Sermons' },
            { value: 'music', label: '🎵 Music Playback' },
            { value: 'choir', label: '🎶 Choir / Vocals' },
            { value: 'live_band', label: '🎸 Live Band / Instruments' },
            { value: 'DJ', label: '🎧 DJ / High Energy' },
            { value: 'video_conference', label: '📹 Video Conferencing' }
          ].map(usage => (
            <div
              key={usage.value}
              className={`usage-chip ${formData.usage.includes(usage.value) ? 'selected' : ''}`}
              onClick={() => handleUsageChange(usage.value)}
            >
              {usage.label}
            </div>
          ))}
        </div>
      </div>

      <div className="budget-section">
        <h4>Budget Preference</h4>
        <div className="budget-options">
          {[
            { value: 'economy', label: 'Economy', desc: 'Budget-friendly, essential features', range: '₹1-3L' },
            { value: 'mid-range', label: 'Mid-Range', desc: 'Balanced quality & value', range: '₹3-8L' },
            { value: 'premium', label: 'Premium', desc: 'High-end, professional grade', range: '₹8L+' }
          ].map(budget => (
            <div
              key={budget.value}
              className={`budget-option ${formData.budget === budget.value ? 'selected' : ''}`}
              onClick={() => setFormData({ ...formData, budget: budget.value })}
            >
              <h5>{budget.label}</h5>
              <p>{budget.desc}</p>
              <span className="price-range">{budget.range}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="form-navigation">
        <button onClick={handleBack} className="btn-secondary">Back</button>
        <button onClick={handleSubmit} disabled={!validateStep(3) || loading} className="btn-primary btn-large">
          {loading ? '🤖 AI Analyzing...' : 'Get Recommendations 🚀'}
        </button>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="results-section">
      <div className="results-header">
        <h1>Your Personalized Recommendations</h1>
        <p>Based on your requirements, here are our top system suggestions</p>
      </div>

      <div className="disclaimer-banner">
        <strong>⚠️ Important:</strong> This is a preliminary recommendation based on typical acoustic calculations. 
        Final system design will be confirmed after site inspection by our engineers.
      </div>

      <div className="recommendations-grid">
        {recommendations.map((rec, index) => (
          <div key={rec.id} className={`recommendation-card ${index === 0 ? 'best-match' : ''}`}>
            {index === 0 && <div className="best-badge">⭐ BEST MATCH (AI Rated)</div>}
            
            <div className="rec-header">
              <h3>{rec.name}</h3>
              <div className="rating">
                {[...Array(rec.rating)].map((_, i) => <FaStar key={i} className="star-filled" />)}
                {[...Array(3 - rec.rating)].map((_, i) => <FaStar key={i} className="star-empty" />)}
              </div>
            </div>

            <div className="rec-price">
              <span className="price-label">Total Investment</span>
              <span className="price-value">₹{(rec.pricing.total / 100000).toFixed(2)} Lakhs</span>
              <span className="price-detail">(including GST & installation)</span>
            </div>

            <div className="rec-equipment">
              <h4>Equipment Included:</h4>
              <ul>
                {rec.equipment.speakers.map((s, i) => (
                  <li key={i}>✓ {s.quantity}× {s.brand} {s.model} {s.type}</li>
                ))}
                <li>✓ {rec.equipment.mixer.brand} {rec.equipment.mixer.model} Mixer</li>
                {rec.equipment.amplifiers.map((a, i) => (
                  <li key={i}>✓ {a.quantity}× {a.brand} {a.model} Amplifier</li>
                ))}
                <li>✓ Professional microphones & accessories</li>
                <li>✓ Complete installation & calibration</li>
              </ul>
            </div>

            <div className="rec-why">
              <h4>Why This System:</h4>
              <p>{rec.aiExplanation || rec.whyRecommended}</p>
            </div>

            <div className="rec-specs">
              <div className="spec-item">
                <span>Coverage</span>
                <strong>{rec.technicalSpecs.coverageEfficiency}</strong>
              </div>
              <div className="spec-item">
                <span>SPL</span>
                <strong>{rec.technicalSpecs.estimatedSPL}</strong>
              </div>
              <div className="spec-item">
                <span>Power</span>
                <strong>{rec.technicalSpecs.powerPerSqFt}</strong>
              </div>
            </div>

            <div className="rec-actions">
              <button className="btn-download" onClick={() => handleDownloadPDF(rec)}>
                <FaDownload /> Download PDF
              </button>
              <button className="btn-whatsapp" onClick={() => handleBookWhatsApp(rec)}>
                <FaWhatsapp /> Book via WhatsApp
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="contact-cta">
        <h3>📞 Speak to Our Engineers</h3>
        <p>Need customization or have questions? Our technical team is here to help!</p>
        <button className="btn-contact" onClick={() => WhatsAppHelper.requestConsultation(formData)}>
          Request Free Consultation
        </button>
      </div>
    </div>
  );

  const renderChatbot = () => (
    <div className="chatbot-container">
      <div className="chatbot-header">
        <FaRobot className="bot-icon" />
        <div>
          <h3>AI System Designer</h3>
          <p>Powered by Google Gemini</p>
        </div>
      </div>

      <div className="chat-messages">
        {chatHistory.map((msg, index) => (
          <div key={index} className={`chat-message ${msg.role}`}>
            <div className="message-content">{msg.content}</div>
          </div>
        ))}
        {loading && <div className="chat-message assistant typing">AI is thinking...</div>}
      </div>

      <form className="chat-input-form" onSubmit={handleChatSubmit}>
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Type your message..."
          disabled={loading}
        />
        <button type="submit" disabled={loading || !chatInput.trim()}>
          Send
        </button>
      </form>
    </div>
  );

  return (
    <div className="system-planner-page">
      {!mode && renderModeSelection()}
      {mode === 'form' && step === 1 && renderFormStep1()}
      {mode === 'form' && step === 2 && renderFormStep2()}
      {mode === 'form' && step === 3 && renderFormStep3()}
      {mode === 'form' && step === 4 && recommendations && renderResults()}
      {mode === 'chatbot' && renderChatbot()}
    </div>
  );
};

export default SystemPlanner;
